import { createRouter, createWebHistory, type Router, type NavigationGuardNext, type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router'
import { useGlobal, useUser } from '@/store'
import { i18n } from '@/i18n'
import { Permission } from '@/types/enums/Permission.ts'
import DashboardView from './views/DashboardView.vue'
import SettingsView from './views/SettingsView.vue'
import NoAccessView from './views/NoAccessView.vue'
import ClientsListView from './views/Clients/ClientsListView.vue'
import UserManagementView from './views/Users/UserManagementView.vue'
import WorkflowListView from './views/Workflows/WorkflowListView.vue'
import WorkflowDetailView from './views/Workflows/WorkflowDetailView.vue'
import WorkflowPublicView from './views/Workflows/WorkflowPublicView.vue'
import SapImportView from './views/Sap/SapImportView.vue'
import NolsImportView from './views/Nols/NolsImportView.vue'
import DeadLettersView from './views/Messaging/DeadLettersView.vue'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: DashboardView,
    meta: {
      titleKey: 'navigation.dashboard',
      requiresAuth: true,
      permissions: [
        Permission.Administrator,
        Permission.ITAdmin,
        Permission.ITManager,
        Permission.BusinessAdmin,
        Permission.DeliveryManager,
        Permission.DeliverySpecialist,
        Permission.KeyAccountManager,
        Permission.ClientAccount,
        Permission.Backoffice,
        Permission.Dealer,
        Permission.FleetManager,
        Permission.Driver,
        Permission.HR,
        Permission.VehicleSupplier,
        Permission.TyreSupplier,
        Permission.SupplierSuperUser,
        Permission.PurchaseSupport,
        Permission.CustomerCare
      ],
    }
  },
  {
    path: '/workflows',
    component: WorkflowListView,
    meta: {
      titleKey: 'navigation.workflows',
      requiresAuth: true,
      permissions: [
        Permission.Administrator,
        Permission.ITAdmin,
        Permission.ITManager,
        Permission.BusinessAdmin,
        Permission.DeliveryManager,
        Permission.DeliverySpecialist,
        Permission.KeyAccountManager,
        Permission.ClientAccount,
        Permission.Backoffice,
        Permission.Dealer,
        Permission.FleetManager,
        Permission.Driver,
        Permission.HR,
        Permission.VehicleSupplier,
        Permission.TyreSupplier,
        Permission.SupplierSuperUser,
        Permission.PurchaseSupport,
        Permission.CustomerCare
      ],
    }
  },
  {
    path: '/workflows/detail/:id',
    component: WorkflowDetailView,
    meta: {
      titleKey: 'navigation.workflow',
      requiresAuth: false,
      permissions: [
        Permission.Administrator,
        Permission.ITAdmin,
        Permission.ITManager,
        Permission.BusinessAdmin,
        Permission.DeliveryManager,
        Permission.DeliverySpecialist,
        Permission.KeyAccountManager,
        Permission.ClientAccount,
        Permission.Backoffice,
        Permission.Dealer,
        Permission.FleetManager,
        Permission.Driver,
        Permission.HR,
        Permission.VehicleSupplier,
        Permission.TyreSupplier,
        Permission.SupplierSuperUser,
        Permission.PurchaseSupport,
        Permission.CustomerCare
      ],
    }
  },
  {
    path: '/workflows/public/:slug',
    component: WorkflowPublicView,
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/settings',
    component: SettingsView,
    meta: {
      titleKey: 'navigation.settings',
      requiresAuth: true,
      permissions: [
        Permission.Administrator,
        Permission.ITAdmin,
        Permission.ITManager,
        Permission.BusinessAdmin,
        Permission.DeliveryManager,
        Permission.DeliverySpecialist,
        Permission.KeyAccountManager,
        Permission.ClientAccount,
        Permission.Backoffice,
        Permission.Dealer,
        Permission.FleetManager,
        Permission.Driver,
        Permission.HR,
        Permission.VehicleSupplier,
        Permission.TyreSupplier,
        Permission.SupplierSuperUser,
        Permission.PurchaseSupport,
        Permission.CustomerCare
      ],
    }
  },
  {
    path: '/clients',
    component: ClientsListView,
    meta: {
      titleKey: 'navigation.clients',
      requiresAuth: true,
      permissions: [
        Permission.BusinessAdmin,
        Permission.ITAdmin,
        Permission.ITManager,
        Permission.CustomerCare
      ],
    }
  },
  {
    path: '/users',
    component: UserManagementView,
    meta: {
      titleKey: 'navigation.users',
      requiresAuth: true,
      permissions: [
        Permission.UserManagement
      ],
    }
  },
  {
    path: '/sap-import',
    component: SapImportView,
    meta: {
      titleKey: 'navigation.sapImport',
      requiresAuth: true,
      permissions: [
        Permission.ITAdmin,
        Permission.ITManager,
        Permission.CustomerCare
      ],
    }
  },
  {
    path: '/nols-import',
    component: NolsImportView,
    meta: {
      titleKey: 'navigation.nolsImport',
      requiresAuth: true,
      permissions: [
        Permission.ITAdmin,
        Permission.ITManager,
        Permission.ClientAccount,
        Permission.KeyAccountManager,
        Permission.Backoffice
      ],
    }
  },
  {
    path: '/dead-letters',
    component: DeadLettersView,
    meta: {
      titleKey: 'navigation.deadLetters',
      requiresAuth: true,
      permissions: [
        Permission.ITAdmin,
        Permission.ITManager,
      ],
    }
  },
  {
    path: '/forbidden/',
    name: 'NoAccess',
    component: NoAccessView,
    meta: {
      titleKey: 'navigation.noAccess'
    }
  },
  {
    path: '/:param(.*)*',
    name: 'notFound',
    redirect: '/'
  }
];

const router: Router = createRouter({
  history: createWebHistory(),
  routes
});

router.beforeEach(async (_to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const userStore = useUser();
  const requiresAuth = _to.meta.requiresAuth !== false;

  if (requiresAuth) {
    const isAuthenticated = await userStore.silentLogin();
    
    if (!isAuthenticated) {
      await userStore.login();
      return;
    }
    
    const permissions = _to.meta.permissions as Permission[] | undefined;
    if (permissions && permissions.length > 0) {
      const hasRequiredPermission = userStore.hasPermission(...permissions);

      if (!hasRequiredPermission) {
        next({ name: 'NoAccess' });
        return;
      }
    }
    
    next();
  } else {
    if (!userStore.keycloak) {
      await userStore.silentLogin(true);
    }
    next();
  }
});

router.afterEach((to) => {
  useGlobal().setLoading(false);
  
  const t = i18n.global.t;
  
  if (to.query.modalExternalReference) {
    document.title = `${to.query.modalExternalReference} | Drivalia eOrders`;
  } else {
    const titleKey = to.meta.titleKey as string | undefined;
    
    document.title = titleKey ? `${t(titleKey)} | Drivalia eOrders` : 'Drivalia - eOrders';
  }
});

export default router;